import { useState } from 'react'
import * as Sentry from '@sentry/tanstackstart-react'
import { CheckCircle2, Loader2, ExternalLink, Download, AlertCircle } from 'lucide-react'
import { useHelperStatus } from '../hooks/useHelperStatus'
import { connectionTokenApi } from '../lib/api'

export type ReadinessMode = 'install' | 'uninstall'

interface DeviceReadinessPanelProps {
  platform: 'ios' | 'android' | null
  deviceName?: string | null
  /** Whether the target device is currently reported as plugged in / detected. */
  deviceConnected: boolean
  mode: ReadinessMode
  actionLabel?: string
  onAction?: () => void
  actionDisabled?: boolean
  /** Render the panel without the primary action button (used inside wizard empty states). */
  hideAction?: boolean
  actionVariant?: 'primary' | 'destructive'
}

function platformLabel(platform: 'mac' | 'windows' | null): string {
  if (platform === 'mac') return 'Mac'
  if (platform === 'windows') return 'Windows'
  return 'this computer'
}

function deviceNoun(platform: 'ios' | 'android' | null): string {
  if (platform === 'android') return 'Android phone'
  if (platform === 'ios') return 'iPhone'
  return 'device'
}

export function DeviceReadinessPanel({
  platform,
  deviceName,
  deviceConnected,
  mode,
  actionLabel,
  onAction,
  actionDisabled,
  hideAction,
  actionVariant,
}: DeviceReadinessPanelProps) {
  const { data: helper } = useHelperStatus()
  const [openingHelper, setOpeningHelper] = useState(false)
  const [openHelperError, setOpenHelperError] = useState<string | null>(null)

  const helperReady = helper?.running === true
  const ready = helperReady && deviceConnected

  const displayName = deviceName || `the ${deviceNoun(platform)}`
  const computerLabel = platformLabel(helper?.platform ?? null)

  const resolvedActionLabel =
    actionLabel ??
    (mode === 'install' ? 'Install Unbreakable' : 'Uninstall Unbreakable')

  const resolvedVariant =
    actionVariant ?? (mode === 'uninstall' ? 'destructive' : 'primary')

  const handleOpenHelper = async () => {
    if (openingHelper) return
    setOpeningHelper(true)
    setOpenHelperError(null)
    try {
      // Mint a short-lived connection token and open the helper via its
      // registered URL scheme. Same flow as first-time setup — the helper
      // accepts unbreakable://connect?token=... whether it's launching
      // fresh or already running, so this brings it to the foreground.
      const { token } = await connectionTokenApi.generateConnectionToken()
      const encoded = encodeURIComponent(token)
      const link = document.createElement('a')
      link.href = `unbreakable://connect?token=${encoded}`
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      Sentry.captureException(err)
      setOpenHelperError(
        err instanceof Error ? err.message : 'Failed to open the Helper app'
      )
    } finally {
      setOpeningHelper(false)
    }
  }

  return (
    <div className="space-y-2">
      {/* Row 1: Helper app */}
      {helperReady ? (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center">
            <CheckCircle2 size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900">Helper app is running</div>
            <div className="text-xs text-emerald-800 mt-0.5 truncate">
              {computerLabel}
              {helper?.signedInAs ? ` · ${helper.signedInAs}` : ''}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center">
            <AlertCircle size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900">Open the Helper app</div>
            <div className="text-xs text-amber-800 mt-0.5">
              Sign in with your Unbreakable account on this computer.
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleOpenHelper}
                disabled={openingHelper}
                className="whitespace-nowrap text-xs px-2.5 py-1 bg-white border border-amber-300 rounded-md hover:bg-amber-100 text-amber-900 font-medium inline-flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {openingHelper ? (
                  <Loader2 size={12} className="motion-safe:animate-spin" />
                ) : (
                  <ExternalLink size={12} />
                )}
                {openingHelper ? 'Opening…' : 'Open Helper'}
              </button>
              <a
                href="/settings/helper-app"
                className="whitespace-nowrap text-xs px-2.5 py-1 bg-white border border-gray-200 rounded-md hover:bg-gray-50 text-gray-600 inline-flex items-center gap-1"
              >
                <Download size={12} />
                Not installed?
              </a>
            </div>
            {openHelperError && (
              <p className="text-xs text-red-600 mt-1.5">{openHelperError}</p>
            )}
          </div>
        </div>
      )}

      {/* Row 2: Device connected */}
      {deviceConnected ? (
        <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center">
            <CheckCircle2 size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {deviceName ? `${deviceName} connected` : `${deviceNoun(platform)} connected`}
            </div>
            <div className="text-xs text-emerald-800 mt-0.5">Ready</div>
          </div>
        </div>
      ) : (
        <div
          className={`flex items-start gap-3 p-3 rounded-xl ${
            helperReady
              ? 'bg-amber-50 border border-amber-200'
              : 'bg-gray-50 border border-gray-200 opacity-60'
          }`}
        >
          <div
            className={`flex-shrink-0 w-6 h-6 rounded-full text-white flex items-center justify-center ${
              helperReady ? 'bg-amber-500' : 'bg-gray-300'
            }`}
          >
            {helperReady ? (
              <Loader2 size={14} className="motion-safe:animate-spin" />
            ) : (
              <AlertCircle size={14} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div
              className={`text-sm font-medium ${
                helperReady ? 'text-gray-900' : 'text-gray-600'
              }`}
            >
              Plug {displayName} in via USB
            </div>
            <div
              className={`text-xs mt-0.5 ${
                helperReady ? 'text-amber-800' : 'text-gray-400'
              }`}
            >
              {helperReady
                ? `Waiting for ${deviceName || deviceNoun(platform)}${
                    platform === 'ios' ? '. On the phone, tap Trust when prompted.' : '.'
                  }`
                : 'Waiting for helper app…'}
            </div>
          </div>
        </div>
      )}

      {!hideAction && onAction && (
        <button
          type="button"
          onClick={onAction}
          disabled={!ready || actionDisabled}
          className={
            resolvedVariant === 'destructive'
              ? 'w-full py-2.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
              : 'w-full py-2.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
          }
        >
          {resolvedActionLabel}
        </button>
      )}
    </div>
  )
}
